import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TablePagination,
  TableContainer, TableHead, TableRow, Checkbox, CircularProgress, Alert,
  Button, Paper, Chip, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, useMediaQuery, useTheme, Tabs, Tab,
} from '@mui/material';
import {
  Check as CheckIcon,
  Warning as WarningIcon,
  Payment as PaymentIcon,
  ArrowBack as BackIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ApiService from '../../services/api.service';
import WalletService from '../../services/wallet.service';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { ProTableStats } from '../../components/pro-table';
import { TAB_SX } from '../../components/common/TabbedPage';

const SECTION_TITLE_SX = { fontSize: '0.875rem', fontWeight: 600 };
const BTN_SX = { textTransform: 'none', fontSize: '0.8125rem' };
const STATUS_LABEL = (s) => (s ? s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ') : 'Completed');

const fontSize = {
  xs: '0.75rem',
  sm: '0.8125rem',
  md: '0.875rem',
};

/**
 * Customer-portal Payments page.
 *
 * Lists the caller org's outstanding invoices, lets the user select
 * one or more to pay from their wallet. Allocator runs oldest-first
 * with a single-partial-tail rule (mirrors the TC Settlement
 * Custom mode wizard); checkbox locks once the wallet is exhausted.
 *
 * Optional `?invoice=<id>` query param pre-selects a single invoice
 * (used by the Pay button on PortalInvoiceDetail to keep the
 * single-invoice path one click).
 *
 * If the resulting allocation contains a partial-tail (one invoice
 * funded for less than its amount due), the Pay button opens a
 * confirmation dialog so the customer explicitly authorizes the
 * partial payment.
 */
const PortalPayments = ({ portalUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const orgId = portalUser?.organizationId || portalUser?.internalOrganizationId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [invoices, setInvoices] = useState([]);
  const [walletBalance, setWalletBalance] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [partialWarningOpen, setPartialWarningOpen] = useState(false);

  // Tabs: 0 = Pay (existing flow), 1 = History.
  const [tab, setTab] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(25);

  // History tab state.
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // Pre-select via ?invoice=... query param (deep-link from invoice detail).
  const preselectInvoiceId = searchParams.get('invoice');

  useEffect(() => {
    if (!orgId) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // Pre-select after invoices load.
  useEffect(() => {
    if (preselectInvoiceId && invoices.some((i) => i.id === preselectInvoiceId)) {
      setSelectedIds([preselectInvoiceId]);
    }
  }, [preselectInvoiceId, invoices]);

  // Lazy-load the history tab the first time it's opened. We refetch
  // when the user switches back after a successful payment so the new
  // row shows up.
  useEffect(() => {
    if (tab === 1 && !historyLoaded) {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const res = await ApiService.listPortalInvoicePayments();
      const list = res?.data?.data ?? res?.data ?? [];
      setHistory(Array.isArray(list) ? [...list].sort(byCreatedAtDesc) : []);
      setHistoryLoaded(true);
    } catch (err) {
      setHistoryError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Failed to load payment history',
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [invRes, wallet] = await Promise.all([
        ApiService.getPortalOutstandingInvoicesForOrg(),
        WalletService.getPortalWallet(),
      ]);
      const list = invRes?.data?.data ?? invRes?.data ?? [];
      setInvoices(
        (Array.isArray(list) ? list : []).map(mapInvoice).sort(byDateAsc),
      );
      setWalletBalance(
        Number(wallet?.balance ?? wallet?.approvedBalance ?? 0),
      );
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  // ---- Allocator (oldest-first, single-partial-tail) ----
  const allocations = useMemo(() => {
    if (walletBalance == null) return {};
    const scope = invoices.filter((inv) => selectedIds.includes(inv.id));
    let remaining = walletBalance;
    const out = {};
    for (const inv of scope) {
      if (remaining <= 0) break;
      const due = inv.amountDue;
      if (due <= 0) continue;
      const pay = Math.min(due, remaining);
      out[inv.id] = round2(pay);
      remaining = round2(remaining - pay);
      if (pay < due) break; // partial-tail — stop
    }
    return out;
  }, [invoices, selectedIds, walletBalance]);

  const totalToPay = useMemo(
    () => round2(Object.values(allocations).reduce((s, v) => s + v, 0)),
    [allocations],
  );

  const walletAfter =
    walletBalance != null ? round2(walletBalance - totalToPay) : 0;

  // Partial-tail check: any allocation that's less than the invoice's full due
  const hasPartialTail = useMemo(() => {
    return invoices.some((inv) => {
      const a = allocations[inv.id];
      return a != null && a > 0 && a < inv.amountDue;
    });
  }, [allocations, invoices]);

  // Unfunded-selected (selected but received no allocation) — happens
  // if user somehow bypasses the lock or the wallet drains between
  // page load and click. The Pay button is disabled in this case;
  // backend would also reject.
  const unfundedSelected = useMemo(() => {
    return selectedIds.filter((id) => !(id in allocations));
  }, [selectedIds, allocations]);

  // Lock unchecked rows once the wallet would be exhausted by the
  // current selection (a partial-tail row exists OR remaining is 0).
  const isSelectionLocked = useMemo(() => {
    if (walletBalance == null) return true;
    return totalToPay >= walletBalance && selectedIds.length > 0;
  }, [walletBalance, totalToPay, selectedIds]);

  const toggleInvoice = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleAll = () => {
    if (selectedIds.length === invoices.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(invoices.map((i) => i.id));
    }
  };

  const canSubmit =
    !submitting &&
    !loading &&
    walletBalance != null &&
    walletBalance > 0 &&
    selectedIds.length > 0 &&
    totalToPay > 0 &&
    unfundedSelected.length === 0;

  const handlePayClick = () => {
    setError(null);
    if (!canSubmit) return;
    if (hasPartialTail) {
      setPartialWarningOpen(true);
    } else {
      setConfirmOpen(true);
    }
  };

  const handlePartialAcknowledged = () => {
    setPartialWarningOpen(false);
    setConfirmOpen(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Send only the invoices that received allocation (i.e. funded).
      const fundedIds = invoices
        .filter((inv) => allocations[inv.id] > 0)
        .map((inv) => inv.id);
      await ApiService.payPortalInvoices({
        invoiceIds: fundedIds,
        expectedTotal: totalToPay,
      });
      setSuccess('Payment recorded successfully.');
      setConfirmOpen(false);
      setHistoryLoaded(false); // force history tab to refetch on open
      setTimeout(() => navigate('/portal/invoices'), 1500);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Failed to record payment',
      );
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (!orgId) {
    return (
      <Box>
        <Alert severity="warning">No organization in session. Please re-login.</Alert>
      </Box>
    );
  }

  const headSx = getBrandedTableHeadSx(theme);
  const balance = walletBalance ?? 0;
  const historyRows = history.slice(historyPage * historyRowsPerPage, historyPage * historyRowsPerPage + historyRowsPerPage);

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{ minHeight: 40, flex: 1 }}
        >
          <Tab icon={<PaymentIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Pay Invoices" sx={TAB_SX} />
          <Tab icon={<HistoryIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="History" sx={TAB_SX} />
        </Tabs>
        {!isMobile && (
          <Button size="small" startIcon={<BackIcon />} onClick={() => navigate('/portal/invoices')} sx={BTN_SX}>
            Back to Invoices
          </Button>
        )}
      </Box>

      {tab === 0 && (
      <>
      <ProTableStats
        loading={walletBalance === null && loading}
        cards={[
          { label: 'Wallet Balance', value: WalletService.formatCurrency(balance), color: balance > 0 ? 'success.main' : balance < 0 ? 'error.main' : 'text.disabled' },
          { label: `Selected to pay · ${selectedIds.length} invoice${selectedIds.length === 1 ? '' : 's'}`, value: WalletService.formatCurrency(totalToPay), color: totalToPay > 0 ? 'warning.main' : 'text.disabled' },
          { label: 'Wallet After', value: WalletService.formatCurrency(walletAfter), color: walletAfter > 0 ? 'success.main' : walletAfter < 0 ? 'error.main' : 'text.disabled' },
        ]}
      />

      {/* Outstanding invoices */}
      <Paper>
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1, minHeight: 48 }}>
          <Typography sx={SECTION_TITLE_SX}>
            Outstanding Invoices ({invoices.length})
          </Typography>
          {invoices.length > 0 && (
            <Button
              variant="contained"
              size="small"
              startIcon={<PaymentIcon sx={{ fontSize: 16 }} />}
              disabled={!canSubmit}
              onClick={handlePayClick}
              sx={BTN_SX}
            >
              {submitting ? <CircularProgress size={18} color="inherit" /> : `Pay ${WalletService.formatCurrency(totalToPay)}`}
            </Button>
          )}
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead sx={headSx}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selectedIds.length === invoices.length && invoices.length > 0}
                    indeterminate={selectedIds.length > 0 && selectedIds.length < invoices.length}
                    onChange={toggleAll}
                    disabled={!walletBalance || walletBalance <= 0}
                    sx={{ color: 'inherit', '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: 'inherit' } }}
                  />
                </TableCell>
                <TableCell>Invoice #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell align="right">Amount Due</TableCell>
                <TableCell align="right">Will Pay</TableCell>
                <TableCell align="center">Status After</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell>
                </TableRow>
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    You have no outstanding invoices.
                  </TableCell>
                </TableRow>
              ) : invoices.map((inv) => {
                const isSelected = selectedIds.includes(inv.id);
                const pay = allocations[inv.id] || 0;
                const checkboxLocked = !isSelected && isSelectionLocked;
                const willStatus = pay > 0
                  ? pay >= inv.amountDue ? 'PAID' : 'PARTIAL'
                  : null;
                return (
                  <TableRow
                    key={inv.id}
                    hover
                    selected={isSelected}
                    sx={{ opacity: isSelected ? 1 : 0.7 }}
                  >
                    <TableCell padding="checkbox">
                      <Tooltip
                        title={
                          checkboxLocked
                            ? 'Wallet covers up to one partial-tail. Uncheck a selection to add another.'
                            : ''
                        }
                        placement="top"
                      >
                        <span>
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            disabled={checkboxLocked || !walletBalance || walletBalance <= 0}
                            onChange={() => toggleInvoice(inv.id)}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer' }}
                      onClick={() => navigate(`/portal/invoices/${inv.id}`)}
                    >
                      {inv.invoiceNumber}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{formatDate(inv.invoiceDate)}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{formatDate(inv.dueDate)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>{WalletService.formatCurrency(inv.amountDue)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: pay > 0 ? 'success.main' : 'text.disabled' }}>
                      {pay > 0 ? WalletService.formatCurrency(pay) : '—'}
                    </TableCell>
                    <TableCell align="center">
                      {willStatus === 'PAID' && (
                        <Chip icon={<CheckIcon sx={{ fontSize: 14 }} />} label="Paid" size="small" color="success" />
                      )}
                      {willStatus === 'PARTIAL' && (
                        <Chip icon={<WarningIcon sx={{ fontSize: 14 }} />} label="Partial" size="small" color="warning" />
                      )}
                      {!willStatus && <Typography component="span" sx={{ fontSize: '0.8125rem', color: 'text.disabled' }}>—</Typography>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </>
      )}

      {tab === 1 && (
        <Paper>
          <Box sx={{ px: 2, py: 1, minHeight: 48, display: 'flex', alignItems: 'center' }}>
            <Typography sx={SECTION_TITLE_SX}>Payment History ({history.length})</Typography>
          </Box>
          {historyError && (
            <Alert severity="error" sx={{ mx: 2, mb: 2 }} onClose={() => setHistoryError(null)}>
              {historyError}
            </Alert>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead sx={headSx}>
                <TableRow>
                  <TableCell>Payment #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="center">Initiated By</TableCell>
                  <TableCell align="center">Invoices</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell>
                  </TableRow>
                ) : history.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No payments yet.</TableCell>
                  </TableRow>
                ) : historyRows.map((p) => {
                  const initiator = p.initiator || (p.settlement_mode ? 'TC' : null);
                  const isCustomer = initiator === 'CUSTOMER';
                  const isCancelled = (p.status || '').toUpperCase() === 'CANCELLED';
                  return (
                    <TableRow key={p.id} hover sx={{ opacity: isCancelled ? 0.6 : 1 }}>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{p.payment_number || '—'}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{formatDateTime(p.created_at)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={isCustomer ? 'Customer' : 'Typing Center'}
                          color={isCustomer ? 'primary' : 'warning'}
                        />
                      </TableCell>
                      <TableCell align="center">{p.invoice_count ?? '—'}</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 600,
                          color: isCancelled ? 'text.secondary' : 'success.main',
                          textDecoration: isCancelled ? 'line-through' : 'none',
                        }}
                      >
                        {WalletService.formatCurrency(Number(p.total_amount) || 0)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={STATUS_LABEL(p.status)} color={isCancelled ? 'error' : 'success'} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {history.length > 0 && (
            <TablePagination
              component="div"
              count={history.length}
              page={historyPage}
              onPageChange={(e, pg) => setHistoryPage(pg)}
              rowsPerPage={historyRowsPerPage}
              onRowsPerPageChange={(e) => { setHistoryRowsPerPage(parseInt(e.target.value, 10)); setHistoryPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          )}
        </Paper>
      )}

      {/* Partial payment warning dialog */}
      <Dialog open={partialWarningOpen} onClose={() => setPartialWarningOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>Partial payment confirmation</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: fontSize.sm, mb: 2 }}>
            Your wallet balance can cover most — but not all — of the selected invoices in
            full. The last invoice will be partially paid.
          </Typography>
          <Box sx={{ bgcolor: 'action.hover', border: '1px solid', borderColor: 'warning.main', borderRadius: 1, p: 2, mb: 1 }}>
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Wallet balance</Typography>
            <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, mb: 1 }}>
              {WalletService.formatCurrency(walletBalance || 0)}
            </Typography>
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>You will pay</Typography>
            <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: 'warning.main' }}>
              {WalletService.formatCurrency(totalToPay)}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
            Continue and confirm in the next step, or cancel to adjust your selection.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPartialWarningOpen(false)} size="small" sx={BTN_SX}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            size="small"
            onClick={handlePartialAcknowledged}
            sx={BTN_SX}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm payment dialog */}
      <Dialog open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>Confirm Payment</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: fontSize.sm, mb: 2 }}>
            Pay <strong>{WalletService.formatCurrency(totalToPay)}</strong> from your wallet
            against {selectedIds.length} invoice{selectedIds.length === 1 ? '' : 's'}?
          </Typography>
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 2, mb: 2 }}>
            {invoices
              .filter((inv) => allocations[inv.id] > 0)
              .map((inv) => (
                <Box key={inv.id} display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography sx={{ fontSize: fontSize.sm }}>
                    {inv.invoiceNumber}
                    {allocations[inv.id] < inv.amountDue && (
                      <Chip label="Partial" size="small" color="warning" sx={{ ml: 1, fontSize: '0.65rem', height: 18 }} />
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: 'success.main' }}>
                    {WalletService.formatCurrency(allocations[inv.id])}
                  </Typography>
                </Box>
              ))}
            <Divider sx={{ my: 1 }} />
            <Box display="flex" justifyContent="space-between">
              <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700 }}>Total</Typography>
              <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: 'success.main' }}>
                {WalletService.formatCurrency(totalToPay)}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
              Wallet before
            </Typography>
            <Typography sx={{ fontSize: fontSize.xs }}>
              {WalletService.formatCurrency(walletBalance || 0)}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
              Wallet after
            </Typography>
            <Typography sx={{ fontSize: fontSize.xs, fontWeight: 700 }}>
              {WalletService.formatCurrency(walletAfter)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} size="small" disabled={submitting} sx={BTN_SX}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={submitting}
            sx={BTN_SX}
          >
            {submitting ? <CircularProgress size={18} color="inherit" /> : 'Confirm Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ---- helpers ----

const round2 = (n) => Math.round(n * 100) / 100;

const byDateAsc = (a, b) =>
  new Date(a.invoiceDate || 0) - new Date(b.invoiceDate || 0);

const byCreatedAtDesc = (a, b) =>
  new Date(b.created_at || 0) - new Date(a.created_at || 0);

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—';

const formatDateTime = (date) =>
  date
    ? new Date(date).toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

// Backend returns snake_case. Map to a uniform shape for the page.
const mapInvoice = (inv) => {
  const grandTotal = Number(inv.grand_total ?? inv.total_amount ?? 0);
  const paidAmount = Number(inv.paid_amount ?? 0);
  return {
    id: inv.id,
    invoiceNumber: inv.invoice_number,
    invoiceDate: inv.invoice_date,
    dueDate: inv.due_date,
    grandTotal,
    paidAmount,
    amountDue:
      Number(inv.amount_due) ||
      round2(grandTotal - paidAmount),
    customerPaymentStatus: inv.customer_payment_status,
  };
};

export default PortalPayments;
